import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { track } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SafeMeAuthProvider, useSafeMeAuth } from "@/context/SafeMeAuthContext";
import { SafeMeAdminAuthProvider, useSafeMeAdminAuth } from "@/context/SafeMeAdminAuthContext";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ClientPortal from "@/pages/ClientPortal";
import PartnerPortal from "@/pages/PartnerPortal";
import AdminDashboard from "@/pages/AdminDashboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import SafeMe from "@/pages/SafeMe";
import SafeMeLogin from "@/pages/SafeMeLogin";
import SafeMeRegister from "@/pages/SafeMeRegister";
import SafeMeAdmin from "@/pages/SafeMeAdmin";
import SafeMeAdminLogin from "@/pages/SafeMeAdminLogin";
import ServicesHub from "@/pages/ServicesHub";
import ServiceDetail from "@/pages/ServiceDetail";
import TrustHub from "@/pages/TrustHub";
import TrustDetail from "@/pages/TrustDetail";
import SolutionsHub from "@/pages/SolutionsHub";
import SolutionDetail from "@/pages/SolutionDetail";
import CompanyPage from "@/pages/CompanyPage";
import ResourcesHub from "@/pages/ResourcesHub";
import ResourceCategory from "@/pages/ResourceCategory";
import CaseStudies from "@/pages/CaseStudies";
import Pricing from "@/pages/Pricing";
import Soc2Journey from "@/pages/Soc2Journey";
import ProductSafeMe from "@/pages/ProductSafeMe";
import ProductSecureDesk from "@/pages/ProductSecureDesk";
import Assessments from "@/pages/Assessments";
import Contact from "@/pages/Contact";
import Cookies from "@/pages/Cookies";
import BaselineReview from "@/pages/BaselineReview";
import Approach from "@/pages/Approach";
import Hipaa from "@/pages/Hipaa";
import ExposureCheck from "@/pages/ExposureCheck";
import { CookieConsent } from "@/components/CookieConsent";
import { LaunchBanner } from "@/components/LaunchBanner";

function SmoothScroll() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    track("page_view", pathname);
  }, [pathname]);
  return null;
}

function ProtectedRoute({ role, loginPath = "/login", children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-fog text-sm tracking-widest uppercase">
        Loading
      </div>
    );
  }
  if (!user) return <Navigate to={loginPath} replace />;
  if (role && user.role !== role && user.role !== "admin") {
    const dest = user.role === "partner" ? "/partner" : "/client";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function SafeMeProtectedRoute({ children }) {
  const { user, loading } = useSafeMeAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-fog text-sm tracking-widest uppercase">
        Loading
      </div>
    );
  }
  if (!user) return <Navigate to="/barecord/login" replace />;
  return children;
}

function SafeMeAdminProtectedRoute({ children }) {
  const { user, loading } = useSafeMeAdminAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-fog text-sm tracking-widest uppercase">
        Loading
      </div>
    );
  }
  if (!user) return <Navigate to="/barecord/admin/login" replace />;
  return children;
}

function App() {
  const isSafeMeHost =
    typeof window !== "undefined" &&
    (window.location.hostname.toLowerCase().startsWith("safeme.") ||
      window.location.hostname.toLowerCase().startsWith("barecord."));
  return (
    <div className="App grain">
      <AuthProvider>
        <SafeMeAuthProvider>
          <SafeMeAdminAuthProvider>
          <BrowserRouter>
          <SmoothScroll />
          <RouteTracker />
          <Routes>
            <Route path="/" element={isSafeMeHost ? <Navigate to="/barecord" replace /> : <Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/security" element={<Navigate to="/trust/security" replace />} />
            <Route path="/services" element={<ServicesHub />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/trust" element={<TrustHub />} />
            <Route path="/trust/:slug" element={<TrustDetail />} />
            <Route path="/solutions" element={<SolutionsHub />} />
            <Route path="/solutions/:slug" element={<SolutionDetail />} />
            <Route path="/company/:slug" element={<CompanyPage />} />
            <Route path="/products/safeme" element={<ProductSafeMe />} />
            <Route path="/products/securedesk" element={<ProductSecureDesk />} />
            <Route path="/assessments" element={<Assessments />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/resources/:slug" element={<ResourceCategory />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/baseline-review" element={<BaselineReview />} />
            <Route path="/approach" element={<Approach />} />
            <Route path="/hipaa" element={<Hipaa />} />
            <Route path="/exposure-check" element={<ExposureCheck />} />
            <Route path="/trust/soc2-journey" element={<Soc2Journey />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route
              path="/client"
              element={
                <ProtectedRoute role="client">
                  <ClientPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner"
              element={
                <ProtectedRoute role="partner">
                  <PartnerPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute role="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            {/* BArecord (formerly SafeME) */}
            <Route path="/barecord/login" element={<SafeMeLogin />} />
            <Route path="/barecord/register" element={<SafeMeRegister />} />
            <Route
              path="/barecord"
              element={
                <SafeMeProtectedRoute>
                  <SafeMe />
                </SafeMeProtectedRoute>
              }
            />
            <Route path="/barecord/admin/login" element={<SafeMeAdminLogin />} />
            <Route
              path="/barecord/admin"
              element={
                <SafeMeAdminProtectedRoute>
                  <SafeMeAdmin />
                </SafeMeAdminProtectedRoute>
              }
            />
            {/* Legacy /safeme redirects -> /barecord */}
            <Route path="/safeme/login" element={<Navigate to="/barecord/login" replace />} />
            <Route path="/safeme/register" element={<Navigate to="/barecord/register" replace />} />
            <Route path="/safeme/admin/login" element={<Navigate to="/barecord/admin/login" replace />} />
            <Route path="/safeme/admin" element={<Navigate to="/barecord/admin" replace />} />
            <Route path="/safeme" element={<Navigate to="/barecord" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <LaunchBanner />
          <Toaster position="bottom-right" theme="dark" />
          <CookieConsent />
        </BrowserRouter>
          </SafeMeAdminAuthProvider>
        </SafeMeAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
